import { useContext, useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import styles from './Home.module.css';
import { HomeContext } from './Home.context';

import {
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	FEATURES_KEY,
} from '../../../views/home/auth/Authentication.hooks';

import { InstallationDialog, LogOutDialog } from '../../../views/home/Home';

import { AuthenticationView } from '../../../views/home/Authentication';

import { Icon, IconType } from '../../../components/icon/Icon';
import { Issue, NAVBAR_ICON_URL } from '../../../constants';

import config from '../../../config';
import { TemplateSectionItem, TemplateSections } from '../../../config.type';
import { Installation } from '../../../views/installation/Installation';

import { useIssues, byReleaseDate } from '../../../hooks/use-issue';

import { Modal } from '../../../components/modal/Modal';

import { SingleIssue } from './SingleIssue';
import { MultipleIssue } from './MultipleIssue';

const template = config?.app?.template as TemplateSections;
const defaultSection = template?.config?.default;

const sectionsMap: Map<string, TemplateSectionItem> = defaultSection
	? template.config?.sections.reduce(reduceSection, new Map())
	: new Map();

export const Home = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const s = searchParams.get('section') || defaultSection;
	const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] =
		useState(false);
	const [isAuthenticated, setIsAuthenticated] = useState(
		config.app.auth.enabled ? !!localStorage.getItem(AUTH_KEY) : true,
	);

	const validSection = defaultSection
		? template?.config?.sections.reduce((acc: Set<string>, s) => {
				acc.add(s.id);
				return acc;
		  }, new Set<string>())
		: new Set();

	const userFeatures: Set<string> = useMemo(() => {
		const features = localStorage.getItem(FEATURES_KEY);
		if (!features) {
			return new Set();
		}
		return new Set([...JSON.parse(features)]);
	}, [isAuthenticated]);
	const isValidSection = validSection.has(s);
	const [section, setSection] = useState<string>(
		isValidSection ? s : defaultSection,
	);

	const [showInstallation, setShowInstallation] = useState(false);
	const [showLogout, setShowLogout] = useState(false);
	const onChangeSection = (selected: string) => {
		setSection(selected);
		setSearchParams({
			section: selected,
		});
	};

	useEffect(() => {
		setSearchParams({
			section,
		});
	}, [section]);

	const onClickDisabledIssue = (issue: Issue) => {
		setIsSubscriptionDialogOpen(true);
		/*alert(
			'This user do not have access to see the content please upgrade your subscription',
		);*/
	};

	// Controls Authentication
	const onSuccess = () => {
		localStorage.setItem(AUTH_KEY, 'true');
		setIsAuthenticated(true);
	};
	const onClickLogOut = () => {
		localStorage.removeItem(AUTH_KEY);
		if (config.app.auth.type === 'token') {
			localStorage.removeItem(AUTH_EXPIRATION_DATE_KEY);
		}

		if (localStorage.getItem('favorites')) {
			localStorage.setItem('favorites', '[]');
		}

		setIsAuthenticated(false);
		window.location.href = '/';
	};

	if (
		!isAuthenticated &&
		config.app.auth.enabled &&
		(config.app.auth.type === 'login' || config.app.auth.type === 'jwt')
	) {
		return (
			<>
				<Installation
					show={false}
					onCloseClick={() => {
						setShowInstallation(false);
					}}
				/>
				<AuthenticationView onSuccess={onSuccess} />;
			</>
		);
	}

	return (
		<HomeContext.Provider
			value={{
				selectedSection: section,
				onChangeSection,
				sectionsMap,
				userFeatures,
				setShowLogout,
				onClickDisabledIssue,
				hasUserAccess,
				setIsSubscriptionDialogOpen,
			}}
		>
			<InstallationDialog
				setShowInstallation={setShowInstallation}
				show={showInstallation}
			/>
			<LogOutDialog
				show={showLogout}
				setShowLogout={setShowLogout}
				onClickLogOut={onClickLogOut}
			/>
			<div className={styles['home']}>
				<View />
			</div>
			<SubscritionDialog isOpen={isSubscriptionDialogOpen} />
		</HomeContext.Provider>
	);
};

function reduceSection(
	acc: Map<string, TemplateSectionItem>,
	i: TemplateSectionItem,
): Map<string, TemplateSectionItem> {
	acc.set(i.id, i);
	return acc;
}

function View() {
	return (
		<div className={styles['view']}>
			<Topbar />
			<Content />
			<Navbar />
		</div>
	);
}

export function Topbar() {
	return (
		<div className={styles['topbar']}>
			<div></div>
			<div className={styles['title']}>
				<img src={NAVBAR_ICON_URL} alt="Logo" />
			</div>
			<div>
				<Navbar />
			</div>
		</div>
	);
}

export function Content() {
	const { issues, isLoading } = useIssues();
	const { selectedSection, sectionsMap } = useContext(HomeContext);
	const filteredIssues = useMemo(() => {
		const sectionItem = sectionsMap.get(selectedSection);
		if (!sectionItem) {
			return [];
		}

		return issues.filter(filterSection(sectionItem)).sort(byReleaseDate);
	}, [selectedSection, issues]);

	if (isLoading) {
		return (
			<div className={styles['content']}>
				<Loading />
			</div>
		);
	}

	if (!filteredIssues.length) {
		return <div className={styles['content']}>Not found</div>;
	}

	return (
		<div className={styles['content']}>
			{filteredIssues.length === 1 ? (
				<SingleIssue issue={filteredIssues[0]} />
			) : (
				<MultipleIssue issues={filteredIssues} />
			)}
		</div>
	);
}

function filterSection(
	sectionItem: TemplateSectionItem,
): (i: Issue) => boolean {
	const defaultMatch = 'include';
	return (i: Issue): boolean => {
		if (sectionItem.isFree === true && i.isFree === true) {
			return true;
		}

		const match = sectionItem.match ? sectionItem.match : defaultMatch;
		const sectionFeatures = new Set([...sectionItem.features]);
		const issueFeatures = new Set([...i.features]);

		/*
			If the match is include, all the feature values must exist in
			the features for the issue
		*/

		if (match === 'include') {
			const targetMatches = sectionFeatures.size;
			let matchesCount = 0;
			for (const k of [...sectionFeatures]) {
				if (issueFeatures.has(k)) {
					matchesCount += 1;
				}
			}

			return matchesCount === targetMatches;
		}

		/*
			The features in the section item must be an exact match, so
			they must fulfill the following conditions
			1. Must be of the same length
			2. Each key must exist in the other

			If any of this conditions is not fulfilled it not valid
		*/

		if (match === 'exact') {
			// Check the first condition
			if (sectionFeatures.size !== issueFeatures.size) {
				return false;
			}

			// Check the second condition
			for (const k of [...sectionFeatures]) {
				if (!issueFeatures.has(k)) {
					return false;
				}
			}
			return true;
		}

		return true;
	};
}

export function Navbar() {
	const sections = template.config.sections;
	return (
		<nav className={styles['navbar']}>
			<div>
				{sections.map(mapSection)}
				<ButtonLogOut />
			</div>
		</nav>
	);
}

function Loading() {
	return (
		<div className={styles['loading']}>
			<div />
		</div>
	);
}

export const ButtonLogOut = () => {
	const { setShowLogout } = useContext(HomeContext);
	if (!config.app.auth.enabled) {
		return null;
	}

	return (
		<button
			type={'button'}
			title="Log out"
			key={'log-out'}
			data-text="Log out"
			onClick={() => {
				setShowLogout(true);
			}}
			className={'tooltip down'}
		>
			<Icon color="#000" name="logout" />
			<small>Log Out</small>
		</button>
	);
};

function mapSection({ id, name, icon }: TemplateSectionItem) {
	return <SectionButton key={id} id={id} name={name} icon={icon} />;
}

export function SectionButton({ id, name, icon }: SectionButtonProps) {
	const { selectedSection, onChangeSection } = useContext(HomeContext);
	const isSelected = selectedSection === id;
	const className = ['tooltip', 'down'];
	if (isSelected) {
		className.push(styles['selected']);
	}
	return (
		<button
			title={name}
			data-text={name}
			type={'button'}
			className={className.join(' ')}
			onClick={() => {
				onChangeSection(id);
			}}
		>
			<Icon name={icon} />
			<small>{name}</small>
		</button>
	);
}

interface SectionButtonProps {
	id: string;
	name: string;
	icon: IconType;
}

const SubscritionDialog: React.FC<SubscritionDialogProps> = ({
	isOpen,
}: SubscritionDialogProps) => {
	const { setIsSubscriptionDialogOpen } = useContext(HomeContext);

	const { subscriptionUrl, title } = config.build;
	return (
		<Modal
			hasCloseBtn={true}
			isOpen={isOpen}
			onClose={() => {
				setIsSubscriptionDialogOpen(false);
			}}
		>
			<div className={styles['subscription-dialog']}>
				<img src={NAVBAR_ICON_URL} alt="logo" />
				<h3>Subscriber access required</h3>
				<p>
					Subscribe to the digital edition of{' '}
					<em>{title} Magazine</em> to access this content.
				</p>
				{subscriptionUrl ? (
					<button
						type="button"
						onClick={() => {
							open(subscriptionUrl, 'blank');
						}}
					>
						Subscribe Now
					</button>
				) : null}
			</div>
		</Modal>
	);
};

interface SubscritionDialogProps {
	isOpen: boolean;
}

function hasUserAccess(features: Set<string>, issue: Issue) {
	if (issue.isFree) {
		return true;
	}

	if (!issue.features) {
		return true;
	}

	if (issue.features.length === 0) {
		return true;
	}

	const issueFeatures = new Set([...issue.features]);
	for (const feature of issueFeatures) {
		if (features.has(feature)) {
			return true;
		}
	}

	return false;
}
